/**
 * Aineistot (/admin/knowledge): Tietopohjat (every dataset, by what it is; the default) |
 * Dokumentit (KnowledgeManager). The tab and the open dataset live in the query string:
 * ?shape=<text|statements|series> filters the list, ?dataset=<id> opens a text
 * dataset's workspace, ?opinion=<id> an opinion poll (admins only), ?tab=documents
 * the document browser. ?tab=opinions, the old opinion tab, shows the opinion polls.
 * specs/knowledge-kinds.md, specs/dataset-workspace.md, specs/opinion-datasets.md
 */
import React from 'react';
import styled from '@emotion/styled';
import { useSearchParams } from 'react-router-dom';
import AdminHeader from '../../components/AdminHeader';
import KnowledgeManager from '../KnowledgeManager';
import DatasetList from './DatasetWorkspace/DatasetList';
import DatasetWorkspace from './DatasetWorkspace/DatasetWorkspace';
import OpinionDatasetView from './OpinionDatasets/OpinionDatasetView';
import SeriesDatasetView from './DatasetWorkspace/SeriesDatasetView';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useAuth } from '../../components/AuthGate';
import { hasRole } from '../../utils/roles';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%);
`;

const Content = styled.div`
  padding: 32px 48px;
  max-width: 1400px;
  margin: 0 auto;
  @media (max-width: 700px) { padding: 20px 16px; }
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Tab = styled.button`
  padding: 10px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? '#c084fc' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : '#888')};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { color: #fff; }
`;

function KnowledgeTabs({ active, onSelect }) {
  const { t } = useTranslation();
  return (
    <TabBar role="tablist">
      <Tab role="tab" aria-selected={active === 'datasets'} $active={active === 'datasets'} onClick={() => onSelect('datasets')}>
        {t('datasetWorkspace.tabDatasets')}
      </Tab>
      <Tab role="tab" aria-selected={active === 'documents'} $active={active === 'documents'} onClick={() => onSelect('documents')}>
        {t('datasetWorkspace.tabDocuments')}
      </Tab>
    </TabBar>
  );
}

export default function KnowledgeScreen() {
  const [params, setParams] = useSearchParams();
  // Opinion statements datasets are admin work (specs/opinion-datasets.md); a dataset grant opens only text datasets
  const { user } = useAuth();
  const isAdmin = hasRole(user, 'admin');
  const tab = params.get('tab') === 'documents' ? 'documents' : 'datasets';
  const datasetId = params.get('dataset');
  const opinionId = isAdmin ? params.get('opinion') : null;
  const seriesId = params.get('series');
  // ?tab=opinions, the old Mielipideaineistot tab, lands on the list showing opinion polls (#1369)
  const shape = params.get('shape') || (params.get('tab') === 'opinions' ? 'statements' : null);

  const select = (next) => setParams(next === 'datasets' ? {} : { tab: next });
  const openDataset = (id) => setParams(id ? { dataset: id } : {});
  const openOpinion = (id) => setParams(id ? { opinion: id } : { shape: 'statements' });
  const openSeries = (id) => setParams(id ? { series: id } : { shape: 'series' });
  const showShape = (next) => setParams(next ? { shape: next } : {});
  const tabs = <KnowledgeTabs active={tab} onSelect={select} />;

  if (tab === 'documents') return <KnowledgeManager tabs={tabs} />;

  let body;
  if (opinionId) body = <OpinionDatasetView datasetId={opinionId} onBack={() => openOpinion(null)} />;
  else if (seriesId) body = <SeriesDatasetView datasetId={seriesId} onBack={() => openSeries(null)} />;
  else if (datasetId) body = <DatasetWorkspace datasetId={datasetId} onBack={() => openDataset(null)} onOpenDataset={openDataset} />;
  else body = <DatasetList onOpen={openDataset} onOpenOpinion={openOpinion} onOpenSeries={openSeries} shape={shape} onShape={showShape} />;

  return (
    <Container>
      <AdminHeader />
      <Content>
        {tabs}
        {body}
      </Content>
    </Container>
  );
}
